import { createContext, useState, useContext, PropsWithChildren } from 'react';
import { Dropin } from 'braintree-web-drop-in';

interface BraintreeContextType {
	instance: Dropin | null;
	setInstance: (instance: Dropin | null) => void;
	isPaymentDetailsValid: boolean;
	/** Type of the currently-selected Braintree payment method, e.g. 'CreditCard' or 'PayPalAccount'. */
	selectedPaymentMethodType: string | undefined;
}

const BraintreeContext = createContext<BraintreeContextType | undefined>(undefined);

export const useBraintreeContext = () => {
	const context = useContext(BraintreeContext);
	if (!context) {
		throw new Error('useBraintreeContext must be used within a BraintreeProvider');
	}

	return context;
};

export const BraintreeProvider = ({ children }: PropsWithChildren) => {
	const [instance, setInstance] = useState<Dropin | null>(null);
	const [isPaymentDetailsValid, setPaymentDetailsValid] = useState(false);
	const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<string | undefined>(undefined);

	instance?.on('card:validityChange', (payload) => {
		const isValidCard =
			payload.fields.cvv.isValid && payload.fields.number.isValid && payload.fields.expirationDate.isValid;

		setPaymentDetailsValid(isValidCard);
	});

	instance?.on('paymentMethodRequestable', (payload) => {
		setSelectedPaymentMethodType(payload.type);
		if (payload.paymentMethodIsSelected) {
			setPaymentDetailsValid(true);
		}
	});

	instance?.on('noPaymentMethodRequestable', () => {
		setSelectedPaymentMethodType(undefined);
		setPaymentDetailsValid(false);
	});
	return (
		<BraintreeContext.Provider value={{ instance, setInstance, isPaymentDetailsValid, selectedPaymentMethodType }}>
			{children}
		</BraintreeContext.Provider>
	);
};
